import { Globe } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SingleValueProps } from "react-select";
import Select, { components } from "react-select";
import { useQueryClient } from "@tanstack/react-query";
import { setViewerLanguage } from "../../behavior/api/viewer";
import { isLoggedIn } from "../../behavior/security/auth.service";
import { ApplicationLanguage } from "../../constants";
import { setCookie } from "../../utils/cookieHandlers";
import { Text } from "../elements";

interface LanguageSelectorProps {
    isMobile?: boolean;
    language?: string;
}

const options = [
    { value: ApplicationLanguage.EN_EU, label: "English" },
    { value: ApplicationLanguage.SV_SE, label: "Svenska" },
    { value: ApplicationLanguage.DA_DK, label: "Dansk" },
    { value: ApplicationLanguage.DE_DE, label: "Deutsch" },
    { value: ApplicationLanguage.NL_NL, label: "Nederlands" },
    { value: ApplicationLanguage.PT_PT, label: "Português" },
    { value: ApplicationLanguage.TR_TR, label: "Türkçe" },
    { value: ApplicationLanguage.NO_NO, label: "Norsk" },
    { value: ApplicationLanguage.FI_FI, label: "Suomi" },
    { value: ApplicationLanguage.ES_ES, label: "Español" },
    { value: ApplicationLanguage.FR_FR, label: "Français" },
    { value: ApplicationLanguage.EL_GR, label: "Ελληνικά" },
    { value: ApplicationLanguage.PL_PL, label: "Polski" },
    { value: ApplicationLanguage.JA_JP, label: "日本語" },
    { value: ApplicationLanguage.CS_CZ, label: "Čeština" },
    { value: ApplicationLanguage.HU_HU, label: "Magyar" },
    { value: ApplicationLanguage.IT_IT, label: "Italiano" },
    { value: ApplicationLanguage.RO_RO, label: "Română" },
    { value: ApplicationLanguage.SR_RS, label: "Srpski" },
    { value: ApplicationLanguage.SK_SK, label: "Slovenčina" },
    { value: ApplicationLanguage.UK_UA, label: "Українська" },
    { value: ApplicationLanguage.HE_IL, label: "עברית" },
    { value: ApplicationLanguage.AR_001, label: "العربية" },
];

const SingleValue = (props: SingleValueProps<{ value: ApplicationLanguage; label: string }, false>) => {
    return (
        <components.SingleValue {...props}>
            <span style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
                <Globe />
                {props.data.label}
            </span>
        </components.SingleValue>
    );
};

const LanguageSelector = ({ isMobile = false, language }: LanguageSelectorProps) => {
    const { i18n } = useTranslation();
    const queryClient = useQueryClient();
    const [selectedOption, setSelectedOption] = useState(language || i18n.language || ApplicationLanguage.EN_EU);

    useEffect(() => {
        if (language) {
            setSelectedOption(language);
        }
    }, [language]);

    useEffect(() => {
        setSelectedOption(i18n.language);
    }, [i18n.language]);

    const changeLanguage = async (value: string) => {
        if (selectedOption === value) {
            return;
        }

        setSelectedOption(value);
        setCookie("language", value);
        i18n.changeLanguage(value);
        if (isLoggedIn()) {
            await setViewerLanguage(value);
            await queryClient.invalidateQueries({
                queryKey: ["viewer"],
            });
        }
    };

    // Edge translation crash workaround
    // https://github.com/JedWatson/react-select/issues/5816#issuecomment-2120595563
    const emptyAriaLiveMessages = {
        guidance: () => "",
        onChange: () => "",
        onFilter: () => "",
        onFocus: () => "",
    };

    return (
        <div className={isMobile ? "language-mobile-wrapper" : ""}>
            {isMobile && (
                <Text size="large" weight="light" block className="mb-3">
                    Language
                </Text>
            )}
            <Select
                ariaLiveMessages={emptyAriaLiveMessages}
                className="language-selector"
                classNamePrefix="custom"
                options={[...options].sort((a, b) => a.label.localeCompare(b.label))}
                components={{ IndicatorSeparator: undefined, SingleValue }}
                isSearchable={false}
                isRtl={i18n.dir() === "rtl"}
                value={options.find((item) => item.value === selectedOption)}
                onChange={(option) => {
                    if (option) changeLanguage(option.value);
                }}
            />
        </div>
    );
};

export default LanguageSelector;
